import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import AddIcon from '@mui/icons-material/AddOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import StatusChip from '@/components/common/StatusChip';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { PERMISSIONS } from '@/types/auth';
import ClientFormDialog from './ClientFormDialog';
import type { ClientFormValues } from './ClientFormDialog';
import type { ClientDto, CreateClientRequest, UpdateClientRequest } from '@/types/materials';

export default function ClientMaster() {
  const { t } = useTranslation('clientMaster');
  const queryClient = useQueryClient();

  const { data: clients = [], isLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => (await apiClient.get<ClientDto[]>('/clients')).data,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<ClientDto | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<ClientDto | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['clients'] });

  const saveMutation = useMutation({
    mutationFn: (values: ClientFormValues) => {
      if (editingClient) {
        const payload: UpdateClientRequest = values;
        return apiClient.put(`/clients/${editingClient.id}`, payload);
      }
      const payload: CreateClientRequest = values;
      return apiClient.post('/clients', payload);
    },
    onSuccess: () => {
      invalidate();
      setDialogOpen(false);
      setEditingClient(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/clients/${id}`),
    onSuccess: () => {
      invalidate();
      setConfirmDelete(null);
    },
  });

  const columns: DataTableColumn<ClientDto>[] = [
    { field: 'code', headerName: t('table.code') },
    { field: 'name', headerName: t('table.name') },
    { field: 'gstin', headerName: t('table.gstin'), render: (r) => r.gstin ?? '—' },
    { field: 'contactPerson', headerName: t('table.contactPerson'), render: (r) => r.contactPerson ?? '—' },
    { field: 'phone', headerName: t('table.phone'), render: (r) => r.phone ?? '—' },
    { field: 'status', headerName: t('table.status'), render: (r) => <StatusChip status={r.status} />, filterable: true },
    {
      field: 'actions',
      headerName: '',
      align: 'right',
      render: (r) => (
        <IfPermitted permission={PERMISSIONS.CLIENT_MANAGE}>
          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
            <Button
              size="small"
              onClick={() => {
                setEditingClient(r);
                setDialogOpen(true);
              }}
            >
              {t('edit')}
            </Button>
            <Button size="small" color="error" onClick={() => setConfirmDelete(r)}>
              {t('delete')}
            </Button>
          </Stack>
        </IfPermitted>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.CLIENT_MANAGE}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setEditingClient(null);
                setDialogOpen(true);
              }}
            >
              {t('addClient')}
            </Button>
          </IfPermitted>
        }
      />

      <DataTable columns={columns} rows={clients} loading={isLoading} searchable searchPlaceholder={t('search')} getRowId={(r) => r.id} exportFileName="clients" />

      <ClientFormDialog
        open={dialogOpen}
        client={editingClient}
        onClose={() => {
          setDialogOpen(false);
          setEditingClient(null);
        }}
        onSubmit={(values) => saveMutation.mutate(values)}
        loading={saveMutation.isPending}
      />

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        message={t('deleteClient.message')}
        loading={deleteMutation.isPending}
        onConfirm={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
        onClose={() => setConfirmDelete(null)}
      />
    </>
  );
}
